import { useEffect, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	useCreateHub,
	useDeleteHub,
	useHubs,
	useUpdateHub,
} from "@/hooks/use-catalog";
import type { Hub } from "@/lib/catalog/types";

interface HubFormState {
	code: string;
	name: string;
	city: string;
	region: string;
	isActive: boolean;
	sortOrder: number;
}

const emptyForm = (sortOrder = 0): HubFormState => ({
	code: "",
	name: "",
	city: "",
	region: "",
	isActive: true,
	sortOrder,
});

function HubFormPanel({
	mode,
	initial,
	hubId,
	onDone,
	onCancel,
}: {
	mode: "create" | "edit";
	initial: HubFormState;
	hubId?: string;
	onDone: () => void;
	onCancel?: () => void;
}) {
	const createMut = useCreateHub();
	const updateMut = useUpdateHub(hubId ?? "");
	const [form, setForm] = useState<HubFormState>(initial);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => setForm(initial), [initial]);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		try {
			if (mode === "create") await createMut.mutateAsync(form);
			else await updateMut.mutateAsync(form);
			onDone();
		} catch (err) {
			setError(
				extractErrorMessage(
					err,
					mode === "create" ? "Création impossible" : "Édition impossible",
				),
			);
		}
	};

	const pending = mode === "create" ? createMut.isPending : updateMut.isPending;

	return (
		<form onSubmit={submit} style={{ padding: 16 }}>
			<h3
				style={{
					marginTop: 0,
					fontFamily: "var(--a-font-display)",
					fontSize: 16,
				}}
			>
				{mode === "create" ? "Nouveau hub" : `Éditer ${initial.code}`}
			</h3>
			<FormRow label="Code" helper="2-16 car., ex. YAO">
				<input
					className="aform-i"
					value={form.code}
					onChange={(e) =>
						setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
					}
					required
					disabled={mode === "edit"}
				/>
			</FormRow>
			<FormRow label="Nom">
				<input
					className="aform-i"
					value={form.name}
					onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
					required
				/>
			</FormRow>
			<FormRow label="Ville">
				<input
					className="aform-i"
					value={form.city}
					onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
					required
				/>
			</FormRow>
			<FormRow label="Région">
				<input
					className="aform-i"
					value={form.region}
					onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
				/>
			</FormRow>
			<FormRow label="Ordre d'affichage">
				<input
					type="number"
					className="aform-i"
					value={form.sortOrder}
					onChange={(e) =>
						setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))
					}
				/>
			</FormRow>
			<FormRow label="Statut">
				<label
					style={{
						display: "flex",
						alignItems: "center",
						gap: 6,
						fontSize: 13,
					}}
				>
					<input
						type="checkbox"
						checked={form.isActive}
						onChange={(e) =>
							setForm((f) => ({ ...f, isActive: e.target.checked }))
						}
					/>
					Hub actif
				</label>
			</FormRow>
			{error && (
				<div
					style={{ color: "var(--a-danger)", fontSize: 12, marginBottom: 8 }}
				>
					{error}
				</div>
			)}
			<div style={{ display: "flex", gap: 8 }}>
				{mode === "edit" && (
					<AdminButton variant="ghost" type="button" onClick={onCancel}>
						Annuler
					</AdminButton>
				)}
				<AdminButton variant="primary" type="submit" disabled={pending}>
					<AdminIcon n={mode === "create" ? "plus" : "check"} s={14} />
					{pending
						? mode === "create"
							? "Création…"
							: "Sauvegarde…"
						: mode === "create"
							? "Créer le hub"
							: "Enregistrer"}
				</AdminButton>
			</div>
		</form>
	);
}

export function AdminHubsPage() {
	const { data: hubs = [], isLoading } = useHubs();
	const deleteMut = useDeleteHub();
	const confirm = useConfirm();
	const toast = useToast();
	const [editingId, setEditingId] = useState<string | null>(null);

	const startEdit = (h: Hub) => setEditingId(h.id);
	const stopEdit = () => setEditingId(null);

	const handleDelete = async (h: Hub) => {
		const ok = await confirm({
			title: "Supprimer le hub",
			message: (
				<>Cette action est définitive. Tapez le code du hub pour confirmer.</>
			),
			requireText: h.code,
			confirmLabel: "Supprimer",
			tone: "danger",
		});
		if (!ok) return;
		try {
			await deleteMut.mutateAsync(h.id);
			toast.success(`Hub ${h.code} supprimé.`);
		} catch (err) {
			toast.error(extractErrorMessage(err, "Suppression impossible"));
		}
	};

	const editing = editingId ? hubs.find((h) => h.id === editingId) : null;
	const editingInitial: HubFormState = editing
		? {
				code: editing.code,
				name: editing.name,
				city: editing.city,
				region: editing.region ?? "",
				isActive: editing.isActive,
				sortOrder: editing.sortOrder,
			}
		: emptyForm();

	return (
		<>
			<PageHead
				title="Hubs logistiques"
				sub={`${hubs.length} entrepôt(s) actif(s)`}
			/>

			<div
				style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16 }}
			>
				<Card>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Code</th>
									<th>Nom</th>
									<th>Ville</th>
									<th>Région</th>
									<th>Statut</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{isLoading && (
									<tr>
										<td
											colSpan={6}
											className="muted"
											style={{ textAlign: "center", padding: 20 }}
										>
											Chargement…
										</td>
									</tr>
								)}
								{hubs.map((h) => (
									<tr
										key={h.id}
										style={
											editingId === h.id
												? { background: "var(--a-terra-soft)" }
												: {}
										}
									>
										<td className="mono">{h.code}</td>
										<td>
											<strong>{h.name}</strong>
										</td>
										<td>{h.city}</td>
										<td>{h.region ?? "—"}</td>
										<td>
											<Tag variant={h.isActive ? "success" : "neutral"}>
												{h.isActive ? "Actif" : "Inactif"}
											</Tag>
										</td>
										<td>
											<div style={{ display: "flex", gap: 4 }}>
												<button
													type="button"
													className="top-btn"
													onClick={() => startEdit(h)}
													title="Éditer"
												>
													<AdminIcon n="edit" s={14} />
												</button>
												<button
													type="button"
													className="top-btn"
													onClick={() => handleDelete(h)}
													title="Supprimer"
													style={{ color: "var(--a-danger)" }}
												>
													<AdminIcon n="close" s={14} />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>

				<Card>
					{editing ? (
						<HubFormPanel
							mode="edit"
							hubId={editing.id}
							initial={editingInitial}
							onDone={stopEdit}
							onCancel={stopEdit}
						/>
					) : (
						<HubFormPanel
							mode="create"
							initial={emptyForm(hubs.length)}
							onDone={() => undefined}
						/>
					)}
				</Card>
			</div>
		</>
	);
}
